"use client";

import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useSession, signOut } from "next-auth/react";
import { useToast } from "@/hooks/use-toast";
import { useCartStore } from "@/store/cart-store";

import { User } from "@/types/core/auth";
import { AddressWithRelations } from "@/types/business/address";
import { createAddressSchema } from "@/lib/validations/business/address";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import { AddressAutocomplete } from "@/components/ui/address-autocomplete";
import { Select } from "@/components/ui/select";
import { PhoneInput } from "@/components/ui/phone-input";
import {
  User as UserIcon,
  Mail,
  MapPin,
  Building2,
  LogOut,
  Shield,
  Bell,
  Sun,
  Moon,
  Trash2,
  Loader2,
  Phone,
  Camera,
  Upload,
  Pencil,
  Star,
} from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useTheme } from "@/components/providers/theme-provider";
import { LoadingButton } from "@/components/ui/loading-button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

import CookieSettings from "@/components/cookie-setting";
import { uploadUserAvatar } from "@/server/actions/cloudinary";
import Container from "@/components/ui/container";
import GoogleMapsProvider from "@/components/providers/googleMapsProvider";

// Schema definitions
const userProfileSchema = z.object({
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  email: z.string().email(),
  phone: z.string().nullable(),
  image: z.string().nullable(),
});

const passwordChangeSchema = z
  .object({
    currentPassword: z
      .string()
      .min(8, "Password must be at least 8 characters"),
    newPassword: z.string().min(8, "Password must be at least 8 characters"),
    confirmPassword: z
      .string()
      .min(8, "Password must be at least 8 characters"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

type UserProfileFormData = z.infer<typeof userProfileSchema>;
type PasswordChangeFormData = z.infer<typeof passwordChangeSchema>;

interface UserProfileProps {
  user: User;
  addresses: AddressWithRelations[];
  onUpdate: (
    data: Partial<UserProfileFormData>
  ) => Promise<{ success: boolean } | void>;
  onChangePassword?: (
    currentPassword: string,
    newPassword: string
  ) => Promise<{ success: boolean; error?: string }>;
  onCreateAddress?: (
    data: z.infer<typeof createAddressSchema>
  ) => Promise<AddressWithRelations>;
  onUpdateAddress?: (
    id: string,
    data: AddressWithRelations
  ) => Promise<AddressWithRelations>;
  onDeleteAddress?: (ids: string | string[]) => Promise<void>;
  onDeleteAccount?: () => Promise<{ success: boolean; error?: string }>;
  onRequestAccountDeletion?: (
    email: string
  ) => Promise<{ success: boolean; error?: string }>;
  canManageAddresses?: boolean;
}

// Create a custom type for the new address form
type NewAddressFormData = {
  name: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  address_type: "shipping" | "billing" | "additional";
  label?: string;
  is_primary: boolean;
};

export default function UserProfile({
  user,
  addresses = [],
  onUpdate,
  onChangePassword,
  onCreateAddress,
  onUpdateAddress,
  onDeleteAddress,
  onDeleteAccount,
  onRequestAccountDeletion,
  canManageAddresses = true,
}: UserProfileProps) {
  const { theme, setTheme } = useTheme();
  const [activeSection, setActiveSection] = useState<
    "profile" | "security" | "addresses" | "company" | "cookies" | "preferences"
  >("profile");

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [cachedImage, setCachedImage] = useState<{
    url: string;
    file: File;
  } | null>(null);

  const { data: session, update: updateSession } = useSession();

  // Use session image if available, otherwise fall back to user prop
  const [previewImage, setPreviewImage] = useState<string | undefined>(
    session?.user?.image || user.image || undefined
  );

  // Update preview image when session or user changes
  useEffect(() => {
    // Prioritize session image over user prop image
    const imageToUse = session?.user?.image || user.image;
    setPreviewImage(imageToUse || undefined);
  }, [session?.user?.image, user.image]);

  const defaultValues = {
    first_name: user.first_name || null,
    last_name: user.last_name || null,
    email: user.email,
    image: session?.user?.image || user.image || null,
    phone: user.phone || null,
  };

  const form = useForm<UserProfileFormData>({
    resolver: zodResolver(userProfileSchema),
    defaultValues,
  });

  // Reset form when user data changes
  useEffect(() => {
    const values = {
      first_name: user.first_name || null,
      last_name: user.last_name || null,
      email: user.email,
      image: session?.user?.image || user.image || null,
      phone: user.phone || null,
    };
    form.reset(values);
  }, [user, form, session?.user?.image]);

  const { toast } = useToast();

  const handleAvatarChange = async (file: File | null) => {
    if (!file) return;

    try {
      // Show loading state
      setLoading(true);

      // Create a preview of the image
      const objectUrl = URL.createObjectURL(file);
      setCachedImage({ url: objectUrl, file });
      setPreviewImage(objectUrl);

      // Convert the file to a base64 data URL
      const reader = new FileReader();
      const dataUrlPromise = new Promise<string>((resolve) => {
        reader.onloadend = () => {
          resolve(reader.result as string);
        };
        reader.readAsDataURL(file);
      });

      const dataUrl = await dataUrlPromise;

      // Upload the image immediately
      const uploadResult = await uploadUserAvatar(dataUrl, user.id || "");

      if (!uploadResult.success) {
        throw new Error(uploadResult.error || "Failed to upload image");
      }

      // Update the user profile with the new image URL
      const imageUrl = uploadResult.data?.url || null;
      await onUpdate({ image: imageUrl });

      // Update session with new image
      await updateSession({
        ...session,
        user: {
          ...session?.user,
          image: imageUrl,
        },
      });

      // Update UI
      setPreviewImage(imageUrl || undefined);
      setCachedImage(null);

      // Success message
      toast({
        title: "Profile picture updated",
        description: "Your profile picture has been updated successfully.",
        variant: "success",
      });
    } catch (error) {
      console.error("Error uploading image:", error);
      toast({
        title: "Image upload failed",
        description: "Failed to update profile picture. Please try again.",
        variant: "destructive",
      });

      // Revert to previous image
      setPreviewImage(session?.user?.image || user.image || undefined);
      setCachedImage(null);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (values: UserProfileFormData) => {
    try {
      setLoading(true);

      // Get the dirty fields from the form
      const dirtyFields = form.formState.dirtyFields;

      // Create an object with only the modified fields
      const updatedValues = {} as {
        first_name?: string | null;
        last_name?: string | null;
        email?: string;
        phone?: string | null;
        image?: string | null;
      };

      (Object.keys(dirtyFields) as Array<keyof UserProfileFormData>).forEach(
        (key) => {
          if (dirtyFields[key]) {
            switch (key) {
              case "email":
                if (values[key]) {
                  updatedValues[key] = values[key];
                }
                break;
              case "first_name":
              case "last_name":
              case "phone":
                updatedValues[key] = values[key];
                break;
              case "image":
                // Don't set image here, we'll handle it separately
                break;
            }
          }
        }
      );

      // If image was changed, upload it to Cloudinary first using the server action
      if (cachedImage && cachedImage.file) {
        try {
          // Convert the file to a base64 data URL
          const reader = new FileReader();
          const dataUrlPromise = new Promise<string>((resolve) => {
            reader.onloadend = () => {
              resolve(reader.result as string);
            };
            reader.readAsDataURL(cachedImage.file);
          });

          const dataUrl = await dataUrlPromise;

          // Use the server action to upload the image
          const uploadResult = await uploadUserAvatar(dataUrl, user.id || "");

          if (!uploadResult.success) {
            throw new Error(uploadResult.error || "Failed to upload image");
          }

          // Use the Cloudinary URL instead of the blob URL
          updatedValues.image = uploadResult.data?.url || null;

          // Update the preview image with the Cloudinary URL
          setPreviewImage(uploadResult.data?.url || undefined);
        } catch (uploadError) {
          console.error("Error uploading image:", uploadError);
          toast({
            title: "Image upload failed",
            description: "Failed to upload profile image. Please try again.",
            variant: "destructive",
          });
          setLoading(false);
          return;
        }
      } else if (dirtyFields.image) {
        // If image field was modified but no file was selected (e.g., image was removed)
        updatedValues.image = values.image;
        setPreviewImage(values.image || undefined);
      }

      // Only proceed with update if there are changes
      if (Object.keys(updatedValues).length > 0) {
        await onUpdate(updatedValues);

        // Update the session with the new user data
        await updateSession({
          ...session,
          user: {
            ...session?.user,
            ...updatedValues,
          },
        });

        // Clear the cached image after successful update
        setCachedImage(null);

        setSuccess(true);
        setTimeout(() => setSuccess(false), 2000);

        toast({
          title: "Profile updated successfully",
          description: "Your profile has been updated successfully.",
          variant: "success",
        });
      }

      setIsEditing(false);
    } catch (error) {
      console.error("Error updating profile:", error);
      toast({
        title: "Something went wrong",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Password change logic
  const passwordForm = useForm<PasswordChangeFormData>({
    resolver: zodResolver(passwordChangeSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  const [passwordChangeError, setPasswordChangeError] = useState<string | null>(
    null
  );

  const handlePasswordChange = async (values: PasswordChangeFormData) => {
    if (!onChangePassword) return;

    try {
      setPasswordChangeLoading(true);
      setPasswordChangeError(null);

      const result = await onChangePassword(
        values.currentPassword,
        values.newPassword
      );

      if (!result.success) {
        setPasswordChangeError(result.error || "Failed to change password");
        toast({
          title: "Error",
          description: result.error || "Failed to change password",
          variant: "destructive",
        });
        return;
      }

      // Success
      toast({
        title: "Success",
        description: "Your password has been updated successfully",
        variant: "success",
      });

      // Reset form
      passwordForm.reset();
    } catch (error) {
      console.error("Error changing password:", error);
      setPasswordChangeError(
        error instanceof Error ? error.message : "Failed to change password"
      );
      toast({
        title: "Error",
        description: "Failed to change password",
        variant: "destructive",
      });
    } finally {
      setPasswordChangeLoading(false);
    }
  };

  // Account deletion logic
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isCheckingOrders, setIsCheckingOrders] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  // For address management
  const [addressesState, setAddressesState] =
    useState<AddressWithRelations[]>(addresses);

  // Update addresses when prop changes
  useEffect(() => {
    setAddressesState(addresses);
  }, [addresses]);

  // State for address editing
  const [editingAddress, setEditingAddress] =
    useState<AddressWithRelations | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editForm, setEditForm] = useState({
    label: "",
    name: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "AU",
  });

  // New address form
  const newAddressForm = useForm<NewAddressFormData>({
    defaultValues: {
      name: user.first_name ? `${user.first_name}'s Address` : "My Address",
      address1: "",
      address2: "",
      city: "",
      state: "",
      postal_code: "",
      country: "AU",
      address_type: "shipping",
      label: "Home",
      is_primary: false,
    },
  });

  // Handle adding a new address
  const handleAddAddress = async (data: NewAddressFormData) => {
    if (!onCreateAddress) return;

    setIsSubmitting(true);

    try {
      // Format the data for the API
      const formattedData = {
        name: data.name,
        address1: data.address1,
        address2: data.address2 || undefined,
        city: data.city,
        state: data.state,
        postal_code: data.postal_code,
        country: data.country,
        user_addresses: [
          {
            user_id: user.id as string,
            address_type: data.address_type,
            label: data.label,
            is_primary: data.is_primary,
          },
        ],
      };

      // Create the new address
      const newAddress = await onCreateAddress(formattedData);

      // Update the local state
      setAddressesState((prev) => [...prev, newAddress]);

      // Show success message
      toast({
        title: "Address Added",
        description: "Your new address has been added successfully",
        variant: "success",
      });

      // Close the modal and reset form
      setShowAddModal(false);
      newAddressForm.reset();

      // If this address is set as primary, we need to update other addresses
      if (data.is_primary) {
        // Find any existing primary address of the same type
        const currentPrimaryAddress = addressesState.find((a) =>
          a.users.some(
            (ua) =>
              ua.user_id === user.id &&
              ua.address_type === data.address_type &&
              ua.is_primary
          )
        );

        if (currentPrimaryAddress && onUpdateAddress) {
          // Update the previous primary address to not be primary
          const resetPrimaryUsers = currentPrimaryAddress.users.map((ua) => ({
            ...ua,
            is_primary:
              ua.user_id === user.id && ua.address_type === data.address_type
                ? false
                : ua.is_primary,
          }));

          await onUpdateAddress(currentPrimaryAddress.id, {
            ...currentPrimaryAddress,
            users: resetPrimaryUsers,
          });

          // Update the state
          setAddressesState((prev) =>
            prev.map((a) =>
              a.id === currentPrimaryAddress.id
                ? { ...a, users: resetPrimaryUsers }
                : a
            )
          );
        }
      }
    } catch (err) {
      console.error("Error adding address:", err);
      toast({
        title: "Error",
        description: "Failed to add address",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle address edit
  const handleEditAddress = (address: AddressWithRelations) => {
    setEditingAddress(address);
    const userAddress = address.users.find((ua) => ua.user_id === user.id);

    setEditForm({
      label: userAddress?.label || "",
      name: address.name,
      address1: address.address1,
      address2: address.address2 || "",
      city: address.city,
      state: address.state,
      postal_code: address.postalCode,
      country: address.country,
    });

    setShowEditModal(true);
  };

  // Handle saving edited address
  const handleSaveAddress = async () => {
    if (!onUpdateAddress || !editingAddress) return;

    try {
      // Create updated address object with form data
      const updatedAddress = {
        ...editingAddress,
        name: editForm.name,
        address1: editForm.address1,
        address2: editForm.address2 || null,
        city: editForm.city,
        state: editForm.state,
        postalCode: editForm.postal_code,
        country: editForm.country,
        users: editingAddress.users.map((user) => {
          if (user.user_id === user.id) {
            return {
              ...user,
              label: editForm.label,
            };
          }
          return user;
        }),
      };

      await onUpdateAddress(editingAddress.id, updatedAddress);

      // Update local state
      setAddressesState((prev) =>
        prev.map((a) => (a.id === updatedAddress.id ? updatedAddress : a))
      );

      toast({
        title: "Address Updated",
        description: "Address was updated successfully",
        variant: "success",
      });

      // Close modal
      setShowEditModal(false);
      setEditingAddress(null);
    } catch (err) {
      console.error("Error updating address:", err);
      toast({
        title: "Error",
        description: "Failed to update address",
        variant: "destructive",
      });
    }
  };

  // Function to check for orders before showing delete confirmation
  const handleDeleteClick = async () => {
    if (!onDeleteAccount) return;

    setIsCheckingOrders(true);
    setDeleteError(null);

    try {
      if (onRequestAccountDeletion) {
        const result = await onRequestAccountDeletion(user.email);
        if (!result.success) {
          setDeleteError(result.error || "Failed to process deletion request");
          return;
        }
        toast({
          title: "Deletion Request Sent",
          description: "Please check your email to confirm account deletion.",
          variant: "default",
        });
        return;
      }

      // Fallback to direct deletion if no request handler
      const result = await onDeleteAccount();

      if (!result.success) {
        setDeleteError(result.error || "Failed to check account status");
        return;
      }

      setShowDeleteConfirmation(true);
    } catch (error) {
      console.error("Error checking account status:", error);
      setDeleteError(
        error instanceof Error
          ? error.message
          : "Failed to check account status"
      );
    } finally {
      setIsCheckingOrders(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!onDeleteAccount) return;

    if (deleteConfirmText !== "DELETE") {
      setDeleteError("Please type DELETE to confirm account deletion");
      return;
    }

    try {
      setDeleteLoading(true);
      setDeleteError(null);

      const result = await onDeleteAccount();

      if (!result.success) {
        setDeleteError(result.error || "Failed to delete account");
        return;
      }

      // Dispatch sign-out event for overlay
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("user-signout"));
      }

      // Success - sign out the user
      await signOut({ callbackUrl: "/" });
    } catch (error) {
      console.error("Error deleting account:", error);
      setDeleteError(
        error instanceof Error ? error.message : "Failed to delete account"
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  // Helper function to get initials from name
  const getInitials = () => {
    const firstName = user.first_name || "";
    const lastName = user.last_name || "";
    return (
      (firstName.charAt(0) + lastName.charAt(0)).toUpperCase() ||
      user.email.charAt(0).toUpperCase()
    );
  };

  // Clean up display name
  const getDisplayName = () => {
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    if (user.first_name) return user.first_name;
    if (user.last_name) return user.last_name;
    return user.email.split("@")[0];
  };

  // Handle address deletion with confirmation
  const handleDeleteAddress = async (addressId: string) => {
    if (!onDeleteAddress) return;

    try {
      await onDeleteAddress(addressId);

      // Update the local addresses state to remove the deleted address
      setAddressesState((prev) => prev.filter((a) => a.id !== addressId));

      toast({
        title: "Address Deleted",
        description: "Address was removed successfully",
        variant: "success",
      });
    } catch (err) {
      console.error("Error deleting address:", err);
      toast({
        title: "Error",
        description: "Failed to delete address",
        variant: "destructive",
      });
    }
  };

  // Helper function to get country label from country code
  const getCountryLabel = (code: string) => {
    const countries: Record<string, string> = {
      AU: "Australia",
      NZ: "New Zealand",
      US: "United States",
      GB: "United Kingdom",
    };
    return countries[code] || code;
  };

  // Add a dedicated function for setting an address as primary
  const handleSetPrimary = async (
    address: AddressWithRelations,
    addressType: "shipping" | "billing"
  ) => {
    if (!onUpdateAddress) return;

    try {
      // Find the user's association with this address
      const userAddress = address.users.find((ua) => ua.user_id === user.id);
      if (!userAddress) return;

      // Check if we're toggling primary status off
      const isTogglingOff =
        userAddress.address_type === addressType && userAddress.is_primary;

      // First, find any existing primary address of this type
      const currentPrimaryAddress = addressesState.find((a) =>
        a.users.some(
          (ua) =>
            ua.user_id === user.id &&
            ua.address_type === addressType &&
            ua.is_primary
        )
      );

      // Prepare the data for the backend - It expects user_addresses not users
      const updatedUsersArray = address.users.map((ua) => ({
        user_id: ua.user_id,
        address_type: ua.address_type,
        label: ua.label,
        is_primary:
          ua.user_id === user.id && ua.address_type === addressType
            ? !isTogglingOff // Toggle the status
            : ua.is_primary,
      }));

      // Create formatted data for API
      const formattedAddress = {
        ...address,
        user_addresses: updatedUsersArray, // Backend expects user_addresses, not users
      };

      // Send update to server
      await onUpdateAddress(address.id, formattedAddress);

      // If toggling on and there was a different primary address, update it to not be primary
      if (
        !isTogglingOff &&
        currentPrimaryAddress &&
        currentPrimaryAddress.id !== address.id
      ) {
        const resetPrimaryUsersArray = currentPrimaryAddress.users.map(
          (ua) => ({
            user_id: ua.user_id,
            address_type: ua.address_type,
            label: ua.label,
            is_primary:
              ua.user_id === user.id && ua.address_type === addressType
                ? false
                : ua.is_primary,
          })
        );

        const formattedPrimaryAddress = {
          ...currentPrimaryAddress,
          user_addresses: resetPrimaryUsersArray,
        };

        await onUpdateAddress(
          currentPrimaryAddress.id,
          formattedPrimaryAddress
        );
      }

      // Update local state for immediate UI feedback
      setAddressesState((prev) => {
        return prev.map((a) => {
          // Update the toggled address
          if (a.id === address.id) {
            return {
              ...a,
              users: a.users.map((ua) => ({
                ...ua,
                is_primary:
                  ua.user_id === user.id && ua.address_type === addressType
                    ? !isTogglingOff
                    : ua.is_primary,
              })),
            };
          }

          // Reset previous primary if needed
          if (
            !isTogglingOff &&
            currentPrimaryAddress &&
            a.id === currentPrimaryAddress.id
          ) {
            return {
              ...a,
              users: a.users.map((ua) => ({
                ...ua,
                is_primary:
                  ua.user_id === user.id && ua.address_type === addressType
                    ? false
                    : ua.is_primary,
              })),
            };
          }

          return a;
        });
      });

      toast({
        title: "Address Updated",
        description: isTogglingOff
          ? "Primary status removed"
          : "Primary address updated successfully",
        variant: "success",
      });
    } catch (err) {
      console.error("Error updating address:", err);
      toast({
        title: "Error",
        description: "Failed to update address",
        variant: "destructive",
      });
    }
  };

  // Handle sign out with proper localStorage cleanup
  const handleSignOut = async () => {
    try {
      // Dispatch the sign-out event FIRST to show overlay immediately
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("user-signout"));
      }

      toast({
        title: "Signing out",
        description: "Clearing your session and cart data...",
      });

      // First, clear all cart data from Zustand store - do this synchronously
      const cartStore = useCartStore.getState();
      cartStore.clearAllCarts();

      // Clear localStorage data directly and synchronously
      if (typeof window !== "undefined") {
        // Directly remove the items - put this before signOut to ensure it happens
        window.localStorage.removeItem("user-carts");
        window.localStorage.removeItem("active-cart-data");
        window.localStorage.removeItem("cookie-consent-status");
      }

      // No need for timeout, sign out immediately after clearing
      await signOut({
        redirect: false,
        callbackUrl: window.location.origin,
      });

      // Force a complete page reload to clear any in-memory state
      window.location.href = window.location.origin;
    } catch (error) {
      console.error("[UserProfile] Error during sign out:", error);

      // Even if there's an error, try to clear storage anyway
      if (typeof window !== "undefined") {
        window.localStorage.removeItem("user-carts");
        window.localStorage.removeItem("active-cart-data");
      }

      // Fallback to direct navigation in case of error
      window.location.href = window.location.origin;
    }
  };

  return (
    <GoogleMapsProvider>
      <Container className='py-10'>
        <h1 className='text-3xl font-bold mb-6'>My Account</h1>

        <div className='grid gap-6 md:grid-cols-[1fr_2fr]'>
          {/* Left Card - Navigation and Profile Summary */}
          <Card>
            <CardHeader className='text-center'>
              <div className='relative mx-auto mb-4 group'>
                <Avatar className='w-24 h-24'>
                  <AvatarImage
                    src={previewImage || ""}
                    alt={getDisplayName()}
                  />
                  <AvatarFallback>{getInitials()}</AvatarFallback>
                </Avatar>
                <label
                  htmlFor='avatar-upload'
                  className='absolute bottom-0 right-0 p-1.5 bg-primary text-primary-foreground rounded-full cursor-pointer opacity-90 hover:opacity-100 transition-opacity shadow-sm'
                >
                  <Camera className='h-4 w-4' />
                  <input
                    id='avatar-upload'
                    type='file'
                    accept='image/*'
                    className='hidden'
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      if (file) {
                        handleAvatarChange(file);
                        e.target.value = "";
                      }
                    }}
                  />
                </label>
                <div className='absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity'>
                  <label
                    htmlFor='avatar-upload'
                    className='cursor-pointer p-2 bg-background/80 rounded-full'
                  >
                    <Upload className='h-6 w-6 text-primary' />
                  </label>
                </div>
                {loading && (
                  <div className='absolute inset-0 bg-black/30 rounded-full flex items-center justify-center'>
                    <Loader2 className='h-8 w-8 text-primary animate-spin' />
                  </div>
                )}
              </div>
              <CardTitle className='text-3xl'>{getDisplayName()}</CardTitle>
              <CardDescription className='flex items-center justify-center gap-1'>
                <Mail className='h-4 w-4' />
                {user.email}
              </CardDescription>
            </CardHeader>
            <CardContent className='space-y-2 text-sm'>
              {user.phone && (
                <div className='flex items-center gap-2'>
                  <Phone className='h-4 w-4 text-muted-foreground' />
                  <span>{user.phone}</span>
                </div>
              )}
              {session?.user?.companyName && (
                <div className='flex items-center gap-2'>
                  <Building2 className='h-4 w-4 text-muted-foreground' />
                  <span>{session.user.companyName}</span>
                </div>
              )}
            </CardContent>
            <Separator className='my-2' />
            <CardContent className='space-y-2'>
              <Button
                variant={activeSection === "profile" ? "default" : "ghost"}
                className='w-full justify-start'
                onClick={() => setActiveSection("profile")}
              >
                <UserIcon className='mr-2 h-4 w-4' />
                Profile Information
              </Button>
              <Button
                variant={activeSection === "security" ? "default" : "ghost"}
                className='w-full justify-start'
                onClick={() => setActiveSection("security")}
              >
                <Shield className='mr-2 h-4 w-4' />
                Security
              </Button>
              <Button
                variant={activeSection === "addresses" ? "default" : "ghost"}
                className='w-full justify-start'
                onClick={() => setActiveSection("addresses")}
              >
                <MapPin className='mr-2 h-4 w-4' />
                My Addresses
              </Button>
              {user.company_id && (
                <Button
                  variant={activeSection === "company" ? "default" : "ghost"}
                  className='w-full justify-start'
                  onClick={() => setActiveSection("company")}
                >
                  <Building2 className='mr-2 h-4 w-4' />
                  Company
                </Button>
              )}
              <Button
                variant={activeSection === "preferences" ? "default" : "ghost"}
                className='w-full justify-start'
                onClick={() => setActiveSection("preferences")}
              >
                <Bell className='mr-2 h-4 w-4' />
                Preferences
              </Button>
              <Button
                variant={activeSection === "cookies" ? "default" : "ghost"}
                className='w-full justify-start'
                onClick={() => setActiveSection("cookies")}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  className='mr-2 h-4 w-4'
                >
                  <path d='M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5' />
                  <path d='M8.5 8.5v.01' />
                  <path d='M16 15.5v.01' />
                  <path d='M12 12v.01' />
                  <path d='M11 17v.01' />
                  <path d='M7 14v.01' />
                </svg>
                Cookie Settings
              </Button>
            </CardContent>
            <CardFooter>
              <Button
                variant='outline'
                className='w-full flex items-center gap-2'
                onClick={handleSignOut}
              >
                <LogOut className='h-4 w-4' />
                Sign Out
              </Button>
            </CardFooter>
          </Card>

          {/* Right Card - Content based on active section */}
          <div>
            {activeSection === "profile" && (
              <Card>
                <CardHeader>
                  <CardTitle className='text-3xl'>
                    Profile Information
                  </CardTitle>
                  <CardDescription>Update your account details</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={form.handleSubmit(onSubmit)}>
                    <div className='space-y-4'>
                      <div className='space-y-2'>
                        <Label htmlFor='first_name'>First Name</Label>
                        <div className='relative w-full'>
                          <div className='absolute left-3 top-1/2 -translate-y-1/2'>
                            <UserIcon className='h-4 w-4 text-muted-foreground' />
                          </div>
                          {isEditing ? (
                            <Input
                              id='first_name'
                              {...form.register("first_name")}
                              value={form.watch("first_name") || ""}
                              className='w-full pl-9'
                            />
                          ) : (
                            <div className='flex items-center pl-9'>
                              <span>{user.first_name || "Not set"}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className='space-y-2'>
                        <Label htmlFor='last_name'>Last Name</Label>
                        <div className='relative w-full'>
                          <div className='absolute left-3 top-1/2 -translate-y-1/2'>
                            <UserIcon className='h-4 w-4 text-muted-foreground' />
                          </div>
                          {isEditing ? (
                            <Input
                              id='last_name'
                              {...form.register("last_name")}
                              value={form.watch("last_name") || ""}
                              className='w-full pl-9'
                            />
                          ) : (
                            <div className='flex items-center pl-9'>
                              <span>{user.last_name || "Not set"}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className='space-y-2'>
                        <Label htmlFor='email'>Email Address</Label>
                        <div className='relative w-full'>
                          <div className='absolute left-3 top-1/2 -translate-y-1/2'>
                            <Mail className='h-4 w-4 text-muted-foreground' />
                          </div>
                          {isEditing ? (
                            <Input
                              id='email'
                              type='email'
                              {...form.register("email")}
                              className='w-full pl-9'
                            />
                          ) : (
                            <div className='flex items-center pl-9'>
                              <span>{user.email}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className='space-y-2'>
                        <Label htmlFor='phone'>Phone Number</Label>
                        <div className='relative w-full'>
                          <div className='absolute left-3 top-1/2 -translate-y-1/2 z-10'>
                            <Phone className='h-4 w-4 text-muted-foreground' />
                          </div>
                          {isEditing ? (
                            <Controller
                              name='phone'
                              control={form.control}
                              render={({ field }) => (
                                <PhoneInput
                                  id='phone'
                                  value={field.value || ""}
                                  onChange={field.onChange}
                                  defaultCountry='AU'
                                  className='pl-9'
                                />
                              )}
                            />
                          ) : (
                            <div className='flex items-center pl-9'>
                              <span>{user.phone || "Not set"}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {isEditing && (
                      <div className='mt-6 flex justify-end space-x-2'>
                        <Button
                          type='button'
                          variant='outline'
                          onClick={() => setIsEditing(false)}
                        >
                          Cancel
                        </Button>
                        <LoadingButton
                          type='submit'
                          loading={loading}
                          success={success}
                        >
                          Save Changes
                        </LoadingButton>
                      </div>
                    )}
                  </form>
                </CardContent>
                {!isEditing && (
                  <CardFooter>
                    <Button
                      onClick={() => setIsEditing(true)}
                      className='w-full'
                    >
                      Edit Profile
                    </Button>
                  </CardFooter>
                )}
              </Card>
            )}

            {activeSection === "security" && (
              <Card>
                <CardHeader>
                  <CardTitle className='text-3xl'>Security</CardTitle>
                  <CardDescription>
                    Manage your password and account security
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {!showDeleteConfirmation ? (
                    <div className='space-y-6'>
                      <div className='space-y-4'>
                        <h3 className='text-sm font-medium'>Change Password</h3>
                        <Form {...passwordForm}>
                          <form
                            onSubmit={passwordForm.handleSubmit(
                              handlePasswordChange
                            )}
                            className='space-y-4'
                          >
                            <FormField
                              control={passwordForm.control}
                              name='currentPassword'
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Current Password</FormLabel>
                                  <FormControl>
                                    <Input
                                      type='password'
                                      placeholder='Enter current password'
                                      variant='password'
                                      {...field}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={passwordForm.control}
                              name='newPassword'
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>New Password</FormLabel>
                                  <FormControl>
                                    <Input
                                      type='password'
                                      placeholder='Enter new password'
                                      variant='password'
                                      {...field}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={passwordForm.control}
                              name='confirmPassword'
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Confirm New Password</FormLabel>
                                  <FormControl>
                                    <Input
                                      type='password'
                                      placeholder='Confirm new password'
                                      variant='password'
                                      {...field}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            {passwordChangeError && (
                              <p className='text-sm text-destructive'>
                                {passwordChangeError}
                              </p>
                            )}
                            <LoadingButton
                              type='submit'
                              loading={passwordChangeLoading}
                              disabled={passwordChangeLoading}
                              className='w-full'
                            >
                              Update Password
                            </LoadingButton>
                          </form>
                        </Form>
                      </div>

                      <Separator />

                      <div className='space-y-4'>
                        <h3 className='text-3xl font-bold text-destructive'>
                          Danger Zone
                        </h3>
                        <p className='text-sm'>
                          Once you delete your account, there is no going back.
                          Please be certain.
                          <br />
                          <b>
                            *Note if you have any orders, your account cannot be
                            deleted as we need to retain your order history for
                            legal and accounting purposes.
                          </b>
                        </p>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant='destructive'
                              className='w-full'
                              onClick={handleDeleteClick}
                              disabled={isCheckingOrders}
                            >
                              {isCheckingOrders ? (
                                <>
                                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                  Checking Account...
                                </>
                              ) : (
                                <>
                                  <Trash2 className='mr-2 h-4 w-4' />
                                  Delete Account
                                </>
                              )}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                {deleteError?.includes("order")
                                  ? "Unable to Delete Account"
                                  : "Are you absolutely sure?"}
                              </AlertDialogTitle>
                              <AlertDialogDescription className='space-y-2'>
                                {deleteError ? (
                                  <>
                                    <p className='text-destructive font-medium'>
                                      {deleteError}
                                    </p>
                                    {deleteError.includes("order") && (
                                      <p className='text-muted-foreground text-sm'>
                                        While we cannot delete your account due
                                        to legal requirements, you can contact
                                        our support team if you need to discuss
                                        your options or have specific privacy
                                        concerns.
                                      </p>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <p>
                                      This action cannot be undone. This will
                                      permanently delete your account and remove
                                      your data from our servers.
                                    </p>
                                    <div className='space-y-2 mt-4'>
                                      <Label
                                        htmlFor='delete-confirmation'
                                        className='text-destructive'
                                      >
                                        Type &quot;DELETE&quot; to confirm
                                      </Label>
                                      <Input
                                        id='delete-confirmation'
                                        value={deleteConfirmText}
                                        onChange={(e) =>
                                          setDeleteConfirmText(e.target.value)
                                        }
                                        className='border-destructive/50'
                                        placeholder='Type DELETE to confirm'
                                      />
                                    </div>
                                    <p className='text-muted-foreground text-sm mt-2'>
                                      Note: If you have any order history with
                                      us, your account information may need to
                                      be retained for legal and accounting
                                      purposes.
                                    </p>
                                  </>
                                )}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel
                                onClick={() => {
                                  setDeleteConfirmText("");
                                  setDeleteError(null);
                                }}
                              >
                                Cancel
                              </AlertDialogCancel>
                              {!deleteError?.includes("order") && (
                                <AlertDialogAction
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleDeleteConfirm();
                                  }}
                                  className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
                                  disabled={
                                    deleteLoading ||
                                    deleteConfirmText !== "DELETE"
                                  }
                                >
                                  {deleteLoading
                                    ? "Deleting..."
                                    : "Delete Account"}
                                </AlertDialogAction>
                              )}
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  ) : (
                    <div className='space-y-4'>
                      <div className='rounded-lg border border-destructive/50 bg-destructive/5 p-4'>
                        <div className='flex items-start gap-4'>
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            className='h-5 w-5 text-destructive'
                          >
                            <path d='M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z' />
                            <line x1='12' y1='9' x2='12' y2='13' />
                            <line x1='12' y1='17' x2='12.01' y2='17' />
                          </svg>
                          <div>
                            <h3 className='font-medium text-destructive'>
                              Warning: This action cannot be undone
                            </h3>
                            <p className='text-sm text-destructive/80'>
                              Deleting your account will permanently remove all
                              your data, including profile information, order
                              history, and saved preferences. This action cannot
                              be reversed.
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className='space-y-2'>
                        <Label
                          htmlFor='delete-confirmation'
                          className='text-destructive'
                        >
                          Type &quot;DELETE&quot; to confirm
                        </Label>
                        <Input
                          id='delete-confirmation'
                          className='border-destructive/50'
                        />
                      </div>

                      <div className='flex justify-end space-x-2 mt-4'>
                        <Button
                          variant='outline'
                          onClick={() => setShowDeleteConfirmation(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant='destructive'
                          onClick={handleDeleteConfirm}
                          disabled={deleteLoading}
                        >
                          {deleteLoading
                            ? "Deleting..."
                            : "Permanently Delete Account"}
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {activeSection === "addresses" && (
              <>
                <div className='space-y-6'>
                  <Card>
                    <CardHeader>
                      <CardTitle className='text-3xl'>My Addresses</CardTitle>
                      <CardDescription>
                        Manage your shipping and billing addresses
                      </CardDescription>
                    </CardHeader>
                    <CardContent className='space-y-6'>
                      {/* Shipping Addresses */}
                      <div>
                        <h3 className='text-lg font-medium mb-3'>
                          Shipping Addresses
                        </h3>
                        {addressesState.filter((a) =>
                          a.users.some(
                            (u) =>
                              u.user_id === user.id &&
                              u.address_type === "shipping"
                          )
                        ).length > 0 ? (
                          <div className='border rounded-md overflow-hidden'>
                            <table className='w-full table-fixed'>
                              <thead>
                                <tr className='bg-muted'>
                                  <th className='p-3 text-left font-medium text-sm w-4/5'>
                                    Address
                                  </th>
                                  {canManageAddresses && (
                                    <th className='p-3 text-right font-medium text-sm w-1/5'>
                                      Actions
                                    </th>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {addressesState
                                  .filter((a) =>
                                    a.users.some(
                                      (u) =>
                                        u.user_id === user.id &&
                                        u.address_type === "shipping"
                                    )
                                  )
                                  .map((address) => {
                                    // Find the user's association with this address
                                    const userAddress = address.users.find(
                                      (ua) => ua.user_id === user.id
                                    );

                                    const isPrimary = !!userAddress?.is_primary;
                                    const addressLabel =
                                      userAddress?.label || "";

                                    return (
                                      <tr key={address.id} className='border-t'>
                                        <td className='p-3'>
                                          <div>
                                            {addressLabel && (
                                              <div className='text-sm font-medium text-muted-foreground mb-1'>
                                                {addressLabel}
                                              </div>
                                            )}
                                            <div>
                                              <span className='font-medium'>
                                                {address.name}
                                              </span>
                                              <span className='text-muted-foreground mx-1'>
                                                -
                                              </span>
                                              <span className='text-muted-foreground'>
                                                {address.address1}
                                                {address.address2
                                                  ? `, ${address.address2}`
                                                  : ""}
                                                ,{address.city}, {address.state}{" "}
                                                {address.postalCode}
                                                {address.country
                                                  ? `, ${address.country}`
                                                  : ""}
                                              </span>
                                            </div>
                                          </div>
                                        </td>
                                        {canManageAddresses && (
                                          <td className='p-3 text-right'>
                                            <div className='flex justify-end gap-2'>
                                              <Button
                                                variant='ghost'
                                                size='icon'
                                                onClick={() =>
                                                  handleEditAddress(address)
                                                }
                                                className='h-8 w-8'
                                              >
                                                <Pencil className='h-4 w-4' />
                                                <span className='sr-only'>
                                                  Edit
                                                </span>
                                              </Button>

                                              {isPrimary ? (
                                                <Button
                                                  variant='ghost'
                                                  size='icon'
                                                  onClick={() =>
                                                    handleSetPrimary(
                                                      address,
                                                      "shipping"
                                                    )
                                                  }
                                                  className='h-8 w-8 text-primary'
                                                >
                                                  <Star className='h-4 w-4 fill-current' />
                                                  <span className='sr-only'>
                                                    Remove Primary
                                                  </span>
                                                </Button>
                                              ) : (
                                                <Button
                                                  variant='ghost'
                                                  size='icon'
                                                  onClick={() =>
                                                    handleSetPrimary(
                                                      address,
                                                      "shipping"
                                                    )
                                                  }
                                                  className='h-8 w-8 text-primary'
                                                >
                                                  <Star className='h-4 w-4' />
                                                  <span className='sr-only'>
                                                    Set as Primary
                                                  </span>
                                                </Button>
                                              )}

                                              <AlertDialog>
                                                <AlertDialogTrigger asChild>
                                                  <Button
                                                    variant='ghost'
                                                    size='icon'
                                                    className='h-8 w-8 text-destructive'
                                                  >
                                                    <Trash2 className='h-4 w-4' />
                                                    <span className='sr-only'>
                                                      Delete
                                                    </span>
                                                  </Button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent>
                                                  <AlertDialogHeader>
                                                    <AlertDialogTitle>
                                                      Delete Address
                                                    </AlertDialogTitle>
                                                    <AlertDialogDescription>
                                                      Are you sure you want to
                                                      delete this address? This
                                                      action cannot be undone.
                                                    </AlertDialogDescription>
                                                  </AlertDialogHeader>
                                                  <AlertDialogFooter>
                                                    <AlertDialogCancel>
                                                      Cancel
                                                    </AlertDialogCancel>
                                                    <AlertDialogAction
                                                      onClick={() =>
                                                        handleDeleteAddress(
                                                          address.id
                                                        )
                                                      }
                                                      className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
                                                    >
                                                      Delete
                                                    </AlertDialogAction>
                                                  </AlertDialogFooter>
                                                </AlertDialogContent>
                                              </AlertDialog>
                                            </div>
                                          </td>
                                        )}
                                      </tr>
                                    );
                                  })}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className='text-center py-6 text-muted-foreground border rounded-md'>
                            <MapPin className='h-8 w-8 mx-auto mb-2 opacity-50' />
                            <p>No shipping addresses found</p>
                          </div>
                        )}
                      </div>

                      {/* Billing Addresses */}
                      <div>
                        <h3 className='text-lg font-medium mb-3'>
                          Billing Addresses
                        </h3>
                        {addressesState.filter((a) =>
                          a.users.some(
                            (u) =>
                              u.user_id === user.id &&
                              u.address_type === "billing"
                          )
                        ).length > 0 ? (
                          <div className='border rounded-md overflow-hidden'>
                            <table className='w-full table-fixed'>
                              <thead>
                                <tr className='bg-muted'>
                                  <th className='p-3 text-left font-medium text-sm w-4/5'>
                                    Address
                                  </th>
                                  {canManageAddresses && (
                                    <th className='p-3 text-right font-medium text-sm w-1/5'>
                                      Actions
                                    </th>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {addressesState
                                  .filter((a) =>
                                    a.users.some(
                                      (u) =>
                                        u.user_id === user.id &&
                                        u.address_type === "billing"
                                    )
                                  )
                                  .map((address) => {
                                    // Find the user's association with this address
                                    const userAddress = address.users.find(
                                      (ua) => ua.user_id === user.id
                                    );

                                    const isPrimary = !!userAddress?.is_primary;
                                    const addressLabel =
                                      userAddress?.label || "";

                                    return (
                                      <tr key={address.id} className='border-t'>
                                        <td className='p-3'>
                                          <div>
                                            {addressLabel && (
                                              <div className='text-sm font-medium text-muted-foreground mb-1'>
                                                {addressLabel}
                                              </div>
                                            )}
                                            <div>
                                              <span className='font-medium'>
                                                {address.name}
                                              </span>
                                              <span className='text-muted-foreground mx-1'>
                                                -
                                              </span>
                                              <span className='text-muted-foreground'>
                                                {address.address1}
                                                {address.address2
                                                  ? `, ${address.address2}`
                                                  : ""}
                                                ,{address.city}, {address.state}{" "}
                                                {address.postalCode}
                                                {address.country
                                                  ? `, ${address.country}`
                                                  : ""}
                                              </span>
                                            </div>
                                          </div>
                                        </td>
                                        {canManageAddresses && (
                                          <td className='p-3 text-right'>
                                            <div className='flex justify-end gap-2'>
                                              <Button
                                                variant='ghost'
                                                size='icon'
                                                onClick={() =>
                                                  handleEditAddress(address)
                                                }
                                                className='h-8 w-8'
                                              >
                                                <Pencil className='h-4 w-4' />
                                                <span className='sr-only'>
                                                  Edit
                                                </span>
                                              </Button>

                                              {isPrimary ? (
                                                <Button
                                                  variant='ghost'
                                                  size='icon'
                                                  onClick={() =>
                                                    handleSetPrimary(
                                                      address,
                                                      "billing"
                                                    )
                                                  }
                                                  className='h-8 w-8 text-primary'
                                                >
                                                  <Star className='h-4 w-4 fill-current' />
                                                  <span className='sr-only'>
                                                    Remove Primary
                                                  </span>
                                                </Button>
                                              ) : (
                                                <Button
                                                  variant='ghost'
                                                  size='icon'
                                                  onClick={() =>
                                                    handleSetPrimary(
                                                      address,
                                                      "billing"
                                                    )
                                                  }
                                                  className='h-8 w-8 text-primary'
                                                >
                                                  <Star className='h-4 w-4' />
                                                  <span className='sr-only'>
                                                    Set as Primary
                                                  </span>
                                                </Button>
                                              )}

                                              <AlertDialog>
                                                <AlertDialogTrigger asChild>
                                                  <Button
                                                    variant='ghost'
                                                    size='icon'
                                                    className='h-8 w-8 text-destructive'
                                                  >
                                                    <Trash2 className='h-4 w-4' />
                                                    <span className='sr-only'>
                                                      Delete
                                                    </span>
                                                  </Button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent>
                                                  <AlertDialogHeader>
                                                    <AlertDialogTitle>
                                                      Delete Address
                                                    </AlertDialogTitle>
                                                    <AlertDialogDescription>
                                                      Are you sure you want to
                                                      delete this address? This
                                                      action cannot be undone.
                                                    </AlertDialogDescription>
                                                  </AlertDialogHeader>
                                                  <AlertDialogFooter>
                                                    <AlertDialogCancel>
                                                      Cancel
                                                    </AlertDialogCancel>
                                                    <AlertDialogAction
                                                      onClick={() =>
                                                        handleDeleteAddress(
                                                          address.id
                                                        )
                                                      }
                                                      className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
                                                    >
                                                      Delete
                                                    </AlertDialogAction>
                                                  </AlertDialogFooter>
                                                </AlertDialogContent>
                                              </AlertDialog>
                                            </div>
                                          </td>
                                        )}
                                      </tr>
                                    );
                                  })}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className='text-center py-6 text-muted-foreground border rounded-md'>
                            <MapPin className='h-8 w-8 mx-auto mb-2 opacity-50' />
                            <p>No billing addresses found</p>
                          </div>
                        )}
                      </div>

                      {/* Additional Addresses */}
                      <div>
                        <h3 className='text-lg font-medium mb-3'>
                          Additional Addresses
                        </h3>
                        {addressesState.filter((a) =>
                          a.users.some(
                            (u) =>
                              u.user_id === user.id &&
                              u.address_type === "additional"
                          )
                        ).length > 0 ? (
                          <div className='border rounded-md overflow-hidden'>
                            <table className='w-full table-fixed'>
                              <thead>
                                <tr className='bg-muted'>
                                  <th className='p-3 text-left font-medium text-sm w-4/5'>
                                    Address
                                  </th>
                                  {canManageAddresses && (
                                    <th className='p-3 text-right font-medium text-sm w-1/5'>
                                      Actions
                                    </th>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {addressesState
                                  .filter((a) =>
                                    a.users.some(
                                      (u) =>
                                        u.user_id === user.id &&
                                        u.address_type === "additional"
                                    )
                                  )
                                  .map((address) => {
                                    // Find the user's association with this address
                                    const userAddress = address.users.find(
                                      (ua) => ua.user_id === user.id
                                    );

                                    const addressLabel =
                                      userAddress?.label || "";

                                    return (
                                      <tr key={address.id} className='border-t'>
                                        <td className='p-3'>
                                          <div>
                                            {addressLabel && (
                                              <div className='text-sm font-medium text-muted-foreground mb-1'>
                                                {addressLabel}
                                              </div>
                                            )}
                                            <div>
                                              <span className='font-medium'>
                                                {address.name}
                                              </span>
                                              <span className='text-muted-foreground mx-1'>
                                                -
                                              </span>
                                              <span className='text-muted-foreground'>
                                                {address.address1}
                                                {address.address2
                                                  ? `, ${address.address2}`
                                                  : ""}
                                                ,{address.city}, {address.state}{" "}
                                                {address.postalCode}
                                                {address.country
                                                  ? `, ${address.country}`
                                                  : ""}
                                              </span>
                                            </div>
                                          </div>
                                        </td>
                                        {canManageAddresses && (
                                          <td className='p-3 text-right'>
                                            <div className='flex justify-end gap-2'>
                                              <Button
                                                variant='ghost'
                                                size='icon'
                                                onClick={() =>
                                                  handleEditAddress(address)
                                                }
                                                className='h-8 w-8'
                                              >
                                                <Pencil className='h-4 w-4' />
                                                <span className='sr-only'>
                                                  Edit
                                                </span>
                                              </Button>

                                              <AlertDialog>
                                                <AlertDialogTrigger asChild>
                                                  <Button
                                                    variant='ghost'
                                                    size='icon'
                                                    className='h-8 w-8 text-destructive'
                                                  >
                                                    <Trash2 className='h-4 w-4' />
                                                    <span className='sr-only'>
                                                      Delete
                                                    </span>
                                                  </Button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent>
                                                  <AlertDialogHeader>
                                                    <AlertDialogTitle>
                                                      Delete Address
                                                    </AlertDialogTitle>
                                                    <AlertDialogDescription>
                                                      Are you sure you want to
                                                      delete this address? This
                                                      action cannot be undone.
                                                    </AlertDialogDescription>
                                                  </AlertDialogHeader>
                                                  <AlertDialogFooter>
                                                    <AlertDialogCancel>
                                                      Cancel
                                                    </AlertDialogCancel>
                                                    <AlertDialogAction
                                                      onClick={() =>
                                                        handleDeleteAddress(
                                                          address.id
                                                        )
                                                      }
                                                      className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
                                                    >
                                                      Delete
                                                    </AlertDialogAction>
                                                  </AlertDialogFooter>
                                                </AlertDialogContent>
                                              </AlertDialog>
                                            </div>
                                          </td>
                                        )}
                                      </tr>
                                    );
                                  })}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className='text-center py-6 text-muted-foreground border rounded-md'>
                            <MapPin className='h-8 w-8 mx-auto mb-2 opacity-50' />
                            <p>No additional addresses found</p>
                          </div>
                        )}
                      </div>

                      {canManageAddresses && onCreateAddress && (
                        <div className='mt-4'>
                          <Button
                            className='w-full'
                            onClick={() => setShowAddModal(true)}
                          >
                            <MapPin className='h-4 w-4 mr-2' />
                            Add New Address
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Company Addresses - Moved to its own card */}
                  {addressesState.filter((a) => a.companies?.length > 0)
                    .length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className='text-3xl'>
                          {session?.user?.companyName || "Company"} Addresses
                        </CardTitle>
                        <CardDescription>
                          Addresses associated with your company
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        {/* Company Shipping Addresses */}
                        <div className='space-y-6'>
                          <div>
                            <h3 className='text-lg font-medium mb-3'>
                              Company Shipping Addresses
                            </h3>
                            {addressesState.filter(
                              (a) =>
                                a.companies?.length > 0 &&
                                (a.companies.some(
                                  (c) => c.address_type === "shipping"
                                ) ||
                                  a.users.some(
                                    (u) => u.address_type === "shipping"
                                  ))
                            ).length > 0 ? (
                              <div className='border rounded-md overflow-hidden'>
                                <table className='w-full table-fixed'>
                                  <thead>
                                    <tr className='bg-muted'>
                                      <th className='p-3 text-left font-medium text-sm w-4/5'>
                                        Address
                                      </th>
                                      {canManageAddresses && (
                                        <th className='p-3 text-right font-medium text-sm w-1/5'>
                                          Actions
                                        </th>
                                      )}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {addressesState
                                      .filter(
                                        (a) =>
                                          a.companies?.length > 0 &&
                                          (a.companies.some(
                                            (c) => c.address_type === "shipping"
                                          ) ||
                                            a.users.some(
                                              (u) =>
                                                u.address_type === "shipping"
                                            ))
                                      )
                                      .map((address) => {
                                        const userAddress = address.users.find(
                                          (ua) =>
                                            ua.user_id === user.id &&
                                            ua.address_type === "shipping"
                                        );
                                        const isPrimary =
                                          !!userAddress?.is_primary;

                                        return (
                                          <tr
                                            key={address.id}
                                            className='border-t'
                                          >
                                            <td className='p-3'>
                                              <div>
                                                <div>
                                                  <span className='font-medium'>
                                                    {address.name}
                                                  </span>
                                                  <span className='text-muted-foreground mx-1'>
                                                    -
                                                  </span>
                                                  <span className='text-muted-foreground'>
                                                    {address.address1}
                                                    {address.address2
                                                      ? `, ${address.address2}`
                                                      : ""}
                                                    ,{address.city},{" "}
                                                    {address.state}{" "}
                                                    {address.postalCode}
                                                    {address.country
                                                      ? `, ${address.country}`
                                                      : ""}
                                                  </span>
                                                </div>
                                              </div>
                                            </td>
                                            {canManageAddresses && (
                                              <td className='p-3 text-right'>
                                                <div className='flex justify-end gap-2'>
                                                  <Button
                                                    variant='ghost'
                                                    size='icon'
                                                    onClick={() =>
                                                      handleEditAddress(address)
                                                    }
                                                    className='h-8 w-8'
                                                  >
                                                    <Pencil className='h-4 w-4' />
                                                    <span className='sr-only'>
                                                      Edit
                                                    </span>
                                                  </Button>

                                                  {isPrimary ? (
                                                    <Button
                                                      variant='ghost'
                                                      size='icon'
                                                      onClick={() =>
                                                        handleSetPrimary(
                                                          address,
                                                          "shipping"
                                                        )
                                                      }
                                                      className='h-8 w-8 text-primary'
                                                    >
                                                      <Star className='h-4 w-4 fill-current' />
                                                      <span className='sr-only'>
                                                        Remove Primary
                                                      </span>
                                                    </Button>
                                                  ) : (
                                                    <Button
                                                      variant='ghost'
                                                      size='icon'
                                                      onClick={() =>
                                                        handleSetPrimary(
                                                          address,
                                                          "shipping"
                                                        )
                                                      }
                                                      className='h-8 w-8 text-primary'
                                                    >
                                                      <Star className='h-4 w-4' />
                                                      <span className='sr-only'>
                                                        Set as Primary
                                                      </span>
                                                    </Button>
                                                  )}
                                                </div>
                                              </td>
                                            )}
                                          </tr>
                                        );
                                      })}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div className='text-center py-6 text-muted-foreground border rounded-md'>
                                <MapPin className='h-8 w-8 mx-auto mb-2 opacity-50' />
                                <p>No company shipping addresses found</p>
                              </div>
                            )}
                          </div>

                          {/* Company Billing Addresses */}
                          <div>
                            <h3 className='text-lg font-medium mb-3'>
                              Company Billing Addresses
                            </h3>
                            {addressesState.filter(
                              (a) =>
                                a.companies?.length > 0 &&
                                (a.companies.some(
                                  (c) => c.address_type === "billing"
                                ) ||
                                  a.users.some(
                                    (u) => u.address_type === "billing"
                                  ))
                            ).length > 0 ? (
                              <div className='border rounded-md overflow-hidden'>
                                <table className='w-full table-fixed'>
                                  <thead>
                                    <tr className='bg-muted'>
                                      <th className='p-3 text-left font-medium text-sm w-4/5'>
                                        Address
                                      </th>
                                      {canManageAddresses && (
                                        <th className='p-3 text-right font-medium text-sm w-1/5'>
                                          Actions
                                        </th>
                                      )}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {addressesState
                                      .filter(
                                        (a) =>
                                          a.companies?.length > 0 &&
                                          (a.companies.some(
                                            (c) => c.address_type === "billing"
                                          ) ||
                                            a.users.some(
                                              (u) =>
                                                u.address_type === "billing"
                                            ))
                                      )
                                      .map((address) => {
                                        const userAddress = address.users.find(
                                          (ua) =>
                                            ua.user_id === user.id &&
                                            ua.address_type === "billing"
                                        );
                                        const isPrimary =
                                          !!userAddress?.is_primary;

                                        return (
                                          <tr
                                            key={address.id}
                                            className='border-t'
                                          >
                                            <td className='p-3'>
                                              <div>
                                                <div>
                                                  <span className='font-medium'>
                                                    {address.name}
                                                  </span>
                                                  <span className='text-muted-foreground mx-1'>
                                                    -
                                                  </span>
                                                  <span className='text-muted-foreground'>
                                                    {address.address1}
                                                    {address.address2
                                                      ? `, ${address.address2}`
                                                      : ""}
                                                    ,{address.city},{" "}
                                                    {address.state}{" "}
                                                    {address.postalCode}
                                                    {address.country
                                                      ? `, ${address.country}`
                                                      : ""}
                                                  </span>
                                                </div>
                                              </div>
                                            </td>
                                            {canManageAddresses && (
                                              <td className='p-3 text-right'>
                                                <div className='flex justify-end gap-2'>
                                                  <Button
                                                    variant='ghost'
                                                    size='icon'
                                                    onClick={() =>
                                                      handleEditAddress(address)
                                                    }
                                                    className='h-8 w-8'
                                                  >
                                                    <Pencil className='h-4 w-4' />
                                                    <span className='sr-only'>
                                                      Edit
                                                    </span>
                                                  </Button>

                                                  {isPrimary ? (
                                                    <Button
                                                      variant='ghost'
                                                      size='icon'
                                                      onClick={() =>
                                                        handleSetPrimary(
                                                          address,
                                                          "billing"
                                                        )
                                                      }
                                                      className='h-8 w-8 text-primary'
                                                    >
                                                      <Star className='h-4 w-4 fill-current' />
                                                      <span className='sr-only'>
                                                        Remove Primary
                                                      </span>
                                                    </Button>
                                                  ) : (
                                                    <Button
                                                      variant='ghost'
                                                      size='icon'
                                                      onClick={() =>
                                                        handleSetPrimary(
                                                          address,
                                                          "billing"
                                                        )
                                                      }
                                                      className='h-8 w-8 text-primary'
                                                    >
                                                      <Star className='h-4 w-4' />
                                                      <span className='sr-only'>
                                                        Set as Primary
                                                      </span>
                                                    </Button>
                                                  )}
                                                </div>
                                              </td>
                                            )}
                                          </tr>
                                        );
                                      })}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div className='text-center py-6 text-muted-foreground border rounded-md'>
                                <MapPin className='h-8 w-8 mx-auto mb-2 opacity-50' />
                                <p>No company billing addresses found</p>
                              </div>
                            )}
                          </div>

                          {/* Company Additional Addresses */}
                          <div>
                            <h3 className='text-lg font-medium mb-3'>
                              Company Additional Addresses
                            </h3>
                            {addressesState.filter(
                              (a) =>
                                a.companies?.length > 0 &&
                                (a.companies.some(
                                  (c) => c.address_type === "additional"
                                ) ||
                                  a.users.some(
                                    (u) => u.address_type === "additional"
                                  ))
                            ).length > 0 ? (
                              <div className='border rounded-md overflow-hidden'>
                                <table className='w-full table-fixed'>
                                  <thead>
                                    <tr className='bg-muted'>
                                      <th className='p-3 text-left font-medium text-sm w-4/5'>
                                        Address
                                      </th>
                                      {canManageAddresses && (
                                        <th className='p-3 text-right font-medium text-sm w-1/5'>
                                          Actions
                                        </th>
                                      )}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {addressesState
                                      .filter(
                                        (a) =>
                                          a.companies?.length > 0 &&
                                          (a.companies.some(
                                            (c) =>
                                              c.address_type === "additional"
                                          ) ||
                                            a.users.some(
                                              (u) =>
                                                u.address_type === "additional"
                                            ))
                                      )
                                      .map((address) => {
                                        const userAddress = address.users.find(
                                          (ua) =>
                                            ua.user_id === user.id &&
                                            ua.address_type === "additional"
                                        );
                                        const addressLabel =
                                          userAddress?.label || "";

                                        return (
                                          <tr
                                            key={address.id}
                                            className='border-t'
                                          >
                                            <td className='p-3'>
                                              <div>
                                                {addressLabel && (
                                                  <div className='text-sm font-medium text-muted-foreground mb-1'>
                                                    {addressLabel}
                                                  </div>
                                                )}
                                                <div>
                                                  <span className='font-medium'>
                                                    {address.name}
                                                  </span>
                                                  <span className='text-muted-foreground mx-1'>
                                                    -
                                                  </span>
                                                  <span className='text-muted-foreground'>
                                                    {address.address1}
                                                    {address.address2
                                                      ? `, ${address.address2}`
                                                      : ""}
                                                    ,{address.city},{" "}
                                                    {address.state}{" "}
                                                    {address.postalCode}
                                                    {address.country
                                                      ? `, ${address.country}`
                                                      : ""}
                                                  </span>
                                                </div>
                                              </div>
                                            </td>
                                            {canManageAddresses && (
                                              <td className='p-3 text-right'>
                                                <div className='flex justify-end gap-2'>
                                                  <Button
                                                    variant='ghost'
                                                    size='icon'
                                                    onClick={() =>
                                                      handleEditAddress(address)
                                                    }
                                                    className='h-8 w-8'
                                                  >
                                                    <Pencil className='h-4 w-4' />
                                                    <span className='sr-only'>
                                                      Edit
                                                    </span>
                                                  </Button>
                                                </div>
                                              </td>
                                            )}
                                          </tr>
                                        );
                                      })}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div className='text-center py-6 text-muted-foreground border rounded-md'>
                                <MapPin className='h-8 w-8 mx-auto mb-2 opacity-50' />
                                <p>No company additional addresses found</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Address Edit Dialog */}
                {showEditModal && editingAddress && (
                  <AlertDialog
                    open={showEditModal}
                    onOpenChange={setShowEditModal}
                  >
                    <AlertDialogContent className='max-w-md'>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Edit Address</AlertDialogTitle>
                      </AlertDialogHeader>
                      <div className='space-y-4 py-4'>
                        <div className='grid gap-4'>
                          {/* Only show label field for additional address type */}
                          {editingAddress &&
                            editingAddress.users.some(
                              (ua) =>
                                ua.user_id === user.id &&
                                ua.address_type === "additional"
                            ) && (
                              <div className='grid gap-2'>
                                <Label htmlFor='edit-label'>Label</Label>
                                <Input
                                  id='edit-label'
                                  value={editForm.label}
                                  onChange={(e) =>
                                    setEditForm({
                                      ...editForm,
                                      label: e.target.value,
                                    })
                                  }
                                  placeholder='Home, Office, etc.'
                                />
                              </div>
                            )}
                          <div className='grid gap-2'>
                            <Label htmlFor='edit-name'>Address Name</Label>
                            <Input
                              id='edit-name'
                              value={editForm.name}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  name: e.target.value,
                                })
                              }
                              placeholder='My Address'
                            />
                          </div>
                          <div className='grid gap-2'>
                            <Label htmlFor='edit-address1'>
                              Address Line 1
                            </Label>
                            <AddressAutocomplete
                              value={editForm.address1}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  address1: e.target.value,
                                })
                              }
                              placeholder='Enter your address'
                              onAddressSelect={(address) => {
                                // Update state with all address fields at once
                                setEditForm({
                                  ...editForm,
                                  address1: address.address1,
                                  address2: address.address2 || "",
                                  city: address.city,
                                  state: address.state,
                                  postal_code: address.postalCode,
                                  country: address.country,
                                });

                                // Trigger any form validation or change detection if needed
                                setTimeout(() => {
                                  // Force re-render to ensure UI updates
                                  setEditForm((prev) => ({ ...prev }));
                                }, 100);
                              }}
                            />
                          </div>
                          <div className='grid gap-2'>
                            <Label htmlFor='edit-address2'>
                              Address Line 2
                            </Label>
                            <Input
                              id='edit-address2'
                              value={editForm.address2}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  address2: e.target.value,
                                })
                              }
                              placeholder='Apartment, suite, etc.'
                            />
                          </div>
                          <div className='grid grid-cols-2 gap-4'>
                            <div className='grid gap-2'>
                              <Label htmlFor='edit-city'>City</Label>
                              <Input
                                id='edit-city'
                                value={editForm.city}
                                onChange={(e) =>
                                  setEditForm({
                                    ...editForm,
                                    city: e.target.value,
                                  })
                                }
                              />
                            </div>
                            <div className='grid gap-2'>
                              <Label htmlFor='edit-state'>State</Label>
                              <Select
                                options={[
                                  { value: "NSW", label: "New South Wales" },
                                  { value: "QLD", label: "Queensland" },
                                  { value: "VIC", label: "Victoria" },
                                  {
                                    value: "ACT",
                                    label: "Australian Capital Territory",
                                  },
                                  { value: "TAS", label: "Tasmania" },
                                  { value: "SA", label: "South Australia" },
                                  { value: "WA", label: "Western Australia" },
                                  {
                                    value: "NT",
                                    label: "Northern Territory",
                                  },
                                ]}
                                selected={{
                                  value: editForm.state,
                                  label: editForm.state,
                                }}
                                onChange={(option) => {
                                  const selected = Array.isArray(option)
                                    ? option[0]
                                    : option;
                                  if (selected) {
                                    setEditForm({
                                      ...editForm,
                                      state: selected.value,
                                    });
                                  }
                                }}
                                placeholder='Select state...'
                              />
                            </div>
                          </div>
                          <div className='grid grid-cols-2 gap-4'>
                            <div className='grid gap-2'>
                              <Label htmlFor='edit-postal'>Postal Code</Label>
                              <Input
                                id='edit-postal'
                                value={editForm.postal_code}
                                onChange={(e) =>
                                  setEditForm({
                                    ...editForm,
                                    postal_code: e.target.value,
                                  })
                                }
                              />
                            </div>
                            <div className='grid gap-2'>
                              <Label htmlFor='edit-country'>Country</Label>
                              <Select
                                options={[
                                  { value: "AU", label: "Australia" },
                                  { value: "NZ", label: "New Zealand" },
                                  { value: "US", label: "United States" },
                                  { value: "GB", label: "United Kingdom" },
                                ]}
                                selected={{
                                  value: editForm.country,
                                  label: getCountryLabel(editForm.country),
                                }}
                                onChange={(option) => {
                                  const selected = Array.isArray(option)
                                    ? option[0]
                                    : option;
                                  if (selected) {
                                    setEditForm({
                                      ...editForm,
                                      country: selected.value,
                                    });
                                  }
                                }}
                                placeholder='Select country...'
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <Button onClick={handleSaveAddress}>
                          Save Changes
                        </Button>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}

                {/* Add New Address Dialog */}
                <AlertDialog
                  open={showAddModal}
                  onOpenChange={(open) => {
                    setShowAddModal(open);
                    if (!open) newAddressForm.reset();
                  }}
                >
                  <AlertDialogContent className='max-w-md'>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Add New Address</AlertDialogTitle>
                      <AlertDialogDescription>
                        Enter the details for your new address.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <form
                      onSubmit={newAddressForm.handleSubmit(handleAddAddress)}
                    >
                      <div className='space-y-4 py-4'>
                        <div className='grid gap-4'>
                          <div className='grid gap-2'>
                            <Label htmlFor='address_type'>Address Type</Label>
                            <Controller
                              name='address_type'
                              control={newAddressForm.control}
                              render={({ field }) => (
                                <Select
                                  options={[
                                    { value: "shipping", label: "Shipping" },
                                    { value: "billing", label: "Billing" },
                                    {
                                      value: "additional",
                                      label: "Additional",
                                    },
                                  ]}
                                  selected={{
                                    value: field.value,
                                    label:
                                      field.value.charAt(0).toUpperCase() +
                                      field.value.slice(1),
                                  }}
                                  onChange={(option) => {
                                    const selected = Array.isArray(option)
                                      ? option[0]
                                      : option;
                                    if (selected) {
                                      field.onChange(selected.value);
                                    }
                                  }}
                                  placeholder='Select address type...'
                                />
                              )}
                            />
                          </div>
                          {newAddressForm.watch("address_type") ===
                            "additional" && (
                            <div className='grid gap-2'>
                              <Label htmlFor='label'>Label</Label>
                              <Input
                                id='label'
                                placeholder='Home, Office, etc.'
                                {...newAddressForm.register("label")}
                              />
                            </div>
                          )}
                          <div className='grid gap-2'>
                            <Label htmlFor='name'>Address Name</Label>
                            <Input
                              id='name'
                              placeholder='My Home Address'
                              {...newAddressForm.register("name")}
                              required
                            />
                          </div>
                          <div className='grid gap-2'>
                            <Label htmlFor='address1'>Address Line 1</Label>
                            <Controller
                              name='address1'
                              control={newAddressForm.control}
                              render={({ field }) => (
                                <AddressAutocomplete
                                  {...field}
                                  placeholder='Enter your address'
                                  required
                                  onAddressSelect={(address) => {
                                    // Set all form values in a batch
                                    newAddressForm.setValue(
                                      "address1",
                                      address.address1,
                                      { shouldValidate: true }
                                    );
                                    newAddressForm.setValue(
                                      "address2",
                                      address.address2 || "",
                                      { shouldValidate: true }
                                    );
                                    newAddressForm.setValue(
                                      "city",
                                      address.city,
                                      { shouldValidate: true }
                                    );
                                    newAddressForm.setValue(
                                      "state",
                                      address.state,
                                      { shouldValidate: true }
                                    );
                                    newAddressForm.setValue(
                                      "postal_code",
                                      address.postalCode,
                                      { shouldValidate: true }
                                    );
                                    newAddressForm.setValue(
                                      "country",
                                      address.country,
                                      { shouldValidate: true }
                                    );

                                    // Force form validation and UI update
                                    newAddressForm.trigger([
                                      "address1",
                                      "city",
                                      "state",
                                      "postal_code",
                                      "country",
                                    ]);
                                  }}
                                />
                              )}
                            />
                          </div>
                          <div className='grid gap-2'>
                            <Label htmlFor='address2'>
                              Address Line 2 (Optional)
                            </Label>
                            <Input
                              id='address2'
                              placeholder='Apt 4B'
                              {...newAddressForm.register("address2")}
                            />
                          </div>
                          <div className='grid grid-cols-2 gap-4'>
                            <div className='grid gap-2'>
                              <Label htmlFor='city'>City</Label>
                              <Input
                                id='city'
                                placeholder='Sydney'
                                {...newAddressForm.register("city")}
                                required
                              />
                            </div>
                            <div className='grid gap-2'>
                              <Label htmlFor='state'>State</Label>
                              <Controller
                                name='state'
                                control={newAddressForm.control}
                                render={({ field }) => (
                                  <Select
                                    options={[
                                      {
                                        value: "NSW",
                                        label: "New South Wales",
                                      },
                                      { value: "QLD", label: "Queensland" },
                                      { value: "VIC", label: "Victoria" },
                                      {
                                        value: "ACT",
                                        label: "Australian Capital Territory",
                                      },
                                      { value: "TAS", label: "Tasmania" },
                                      {
                                        value: "SA",
                                        label: "South Australia",
                                      },
                                      {
                                        value: "WA",
                                        label: "Western Australia",
                                      },
                                      {
                                        value: "NT",
                                        label: "Northern Territory",
                                      },
                                    ]}
                                    selected={
                                      field.value
                                        ? {
                                            value: field.value,
                                            label: field.value,
                                          }
                                        : null
                                    }
                                    onChange={(option) => {
                                      const selected = Array.isArray(option)
                                        ? option[0]
                                        : option;
                                      if (selected) {
                                        field.onChange(selected.value);
                                      }
                                    }}
                                    placeholder='Select state...'
                                  />
                                )}
                              />
                            </div>
                          </div>
                          <div className='grid grid-cols-2 gap-4'>
                            <div className='grid gap-2'>
                              <Label htmlFor='postal_code'>Postal Code</Label>
                              <Input
                                id='postal_code'
                                placeholder='2000'
                                {...newAddressForm.register("postal_code")}
                                required
                              />
                            </div>
                            <div className='grid gap-2'>
                              <Label htmlFor='country'>Country</Label>
                              <Controller
                                name='country'
                                control={newAddressForm.control}
                                render={({ field }) => (
                                  <Select
                                    options={[
                                      { value: "AU", label: "Australia" },
                                      { value: "NZ", label: "New Zealand" },
                                      { value: "US", label: "United States" },
                                      {
                                        value: "GB",
                                        label: "United Kingdom",
                                      },
                                    ]}
                                    selected={
                                      field.value
                                        ? {
                                            value: field.value,
                                            label: getCountryLabel(field.value),
                                          }
                                        : null
                                    }
                                    onChange={(option) => {
                                      const selected = Array.isArray(option)
                                        ? option[0]
                                        : option;
                                      if (selected) {
                                        field.onChange(selected.value);
                                      }
                                    }}
                                    placeholder='Select country...'
                                  />
                                )}
                              />
                            </div>
                          </div>
                          <div className='flex items-center space-x-2 pt-2'>
                            <Controller
                              name='is_primary'
                              control={newAddressForm.control}
                              render={({ field }) => (
                                <div className='flex items-center space-x-2'>
                                  <Checkbox
                                    id='is_primary'
                                    checked={field.value}
                                    onCheckedChange={field.onChange}
                                    disabled={
                                      newAddressForm.watch("address_type") ===
                                      "additional"
                                    }
                                  />
                                  <label
                                    htmlFor='is_primary'
                                    className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
                                  >
                                    Set as primary{" "}
                                    {newAddressForm.watch("address_type")}{" "}
                                    address
                                  </label>
                                </div>
                              )}
                            />
                          </div>
                        </div>
                      </div>
                      <AlertDialogFooter>
                        <AlertDialogCancel
                          type='button'
                          onClick={() => {
                            setShowAddModal(false);
                            newAddressForm.reset();
                          }}
                        >
                          Cancel
                        </AlertDialogCancel>
                        <Button type='submit' disabled={isSubmitting}>
                          {isSubmitting ? "Adding..." : "Add Address"}
                        </Button>
                      </AlertDialogFooter>
                    </form>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}

            {activeSection === "company" && user.company_id && (
              <Card>
                <CardHeader>
                  <CardTitle className='text-3xl'>
                    Company Information
                  </CardTitle>
                  <CardDescription>View your company details</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className='space-y-4'>
                    <div className='space-y-2'>
                      <Label>Company Name</Label>
                      <div className='flex items-center gap-2 px-3 py-2 border rounded-md'>
                        <Building2 className='h-4 w-4 text-muted-foreground' />
                        <span>
                          {session?.user?.companyName || "Associated Company"}
                        </span>
                      </div>
                    </div>
                    {session?.user?.jobTitle && (
                      <div className='space-y-2'>
                        <Label>Your Position</Label>
                        <div className='flex items-center gap-2 px-3 py-2 border rounded-md'>
                          <UserIcon className='h-4 w-4 text-muted-foreground' />
                          <span>{session.user.jobTitle}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {activeSection === "preferences" && (
              <Card>
                <CardHeader>
                  <CardTitle className='text-3xl'>Preferences</CardTitle>
                  <CardDescription>Customize your experience</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className='space-y-6'>
                    {/* Theme Selection */}
                    <div className='space-y-4'>
                      <h3 className='text-sm font-medium'>Theme</h3>
                      <RadioGroup
                        defaultValue={theme}
                        onValueChange={(value) =>
                          setTheme(value as "light" | "dark" | "system")
                        }
                        className='flex items-center space-x-4'
                      >
                        <div className='flex items-center space-x-2'>
                          <RadioGroupItem value='light' id='theme-light' />
                          <Label
                            htmlFor='theme-light'
                            className='flex items-center'
                          >
                            <Sun className='mr-2 h-4 w-4' />
                            Light
                          </Label>
                        </div>
                        <div className='flex items-center space-x-2'>
                          <RadioGroupItem value='dark' id='theme-dark' />
                          <Label
                            htmlFor='theme-dark'
                            className='flex items-center'
                          >
                            <Moon className='mr-2 h-4 w-4' />
                            Dark
                          </Label>
                        </div>
                        <div className='flex items-center space-x-2'>
                          <RadioGroupItem value='system' id='theme-system' />
                          <Label htmlFor='theme-system'>System</Label>
                        </div>
                      </RadioGroup>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {activeSection === "cookies" && (
              <Card>
                <CardHeader>
                  <CardTitle className='text-3xl'>Cookie Settings</CardTitle>
                  <CardDescription>
                    Manage how we use cookies on this website
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <CookieSettings />
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </Container>
    </GoogleMapsProvider>
  );
}
